/**
 * Scroll-driven 3D bottle.
 *
 * One glass mesh carries all six fragrances: the label texture and liquid
 * tint change as the page scrolls. Six separate glass meshes would mean six
 * transmission passes, which is the most expensive thing in the scene — so
 * the bottle morphs instead. The label swap is hidden by timing it for when
 * the bottle's face is turned away from camera.
 */
const REDUCED = '(prefers-reduced-motion: reduce)';

// Measured from the product photograph, verified by drawing the detected
// bounds back onto the image. Source figures, in pixels: body 278 x 562,
// cap 251 x 221, label band y345-485, cavity walls x88-300, liquid bottom
// y692, base y825. All constants below are in units of body width.
const W = 1.0;             // body width and depth (square cross-section)
const CORNER = 0.03;       // restrained softening on the vertical edges
// Wall thickness, measured off the base close-up: the bottle is 900px wide
// there and the left wall reads 128px, so the glass is ~0.14 of the body per
// side. Head-on the walls almost vanish, because refraction carries the
// liquid out to the edges and three.js's screen-space transmission does not.
// 0.08 splits the difference — twice the previous wall, still short of the
// measured 0.14, which would read chunky without the refraction to lighten it.
const CAV_W = 0.84;
// The cavity floor is a soft U, not a corner. In the close-up the fillet runs
// ~125px up a 780px cavity, so a sixth of the width.
const CAV_FILLET = 0.14;
const WALL = (W - CAV_W) / 2;
const BASE_H = 0.479;      // thick clear glass base — 0.237 of body height
const SHOULDER_H = 0.03;   // the cap sits almost directly on the glass
const CAV_H = 1.511;       // the 0.02 comes off the shoulder, so BODY_H holds
const NECK_W = 0.42;       // stays inside the cap footprint, so no collar
const NECK_H = 0.03;
// The cap is flush with the glass: measured off the photograph the cap spans
// 167px against a 168px body, i.e. one silhouette, no ledge. It is set a hair
// proud rather than exactly 1.0 for two reasons — the cap overlaps the glass
// shoulder vertically, so equal widths would z-fight along the joint, and
// #prism insets the bottom face by the bevel. 1.016 less two bevels of 0.006
// leaves the cap's bottom edge at 1.004: it covers the glass everywhere they
// meet, with 0.8% of overhang at the widest point, which is under a pixel.
const CAP_W = 1.016;
const CAP_BEVEL = 0.006;   // bottom edge stays wider than the glass
const CAP_H = 0.792;       // 0.39 of the body height, as measured
const LABEL_H = 0.503;     // 0.249 of body height
const LABEL_DROP = 0.295;  // label top, measured down from the shoulder
const FILL = 1;            // brim full — the surface sits under the shoulder

const BODY_H = BASE_H + CAV_H + SHOULDER_H;         // 2.02
const TOTAL_H = BODY_H + CAP_H;

class AzBottle extends HTMLElement {
  connectedCallback() {
    this.canvas = this.querySelector('canvas');
    this.captionEl = this.querySelector('[data-caption]');
    if (!this.canvas) return;

    try {
      this.scents = JSON.parse(this.querySelector('[data-scents]').textContent);
    } catch {
      return;
    }
    if (!this.scents.length) return;

    this.reduced = window.matchMedia(REDUCED).matches;
    this.lowPower =
      window.matchMedia('(max-width: 749px)').matches ||
      (navigator.hardwareConcurrency || 8) <= 4;

    this.index = -1;
    this.progress = 0;
    this.ready = false;

    this.io = new IntersectionObserver(
      (entries) => {
        const visible = entries.some((e) => e.isIntersecting);
        if (visible && !this.ready) this.#init();
        this.visible = visible;
        if (visible) this.#start();
        else this.#stop();
      },
      { rootMargin: '200px' }
    );
    this.io.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.io?.disconnect();
    this.renderer?.dispose();
  }

  // ---- textures -------------------------------------------------------

  /** Warm studio. Flat glass faces mirror this, so it needs real structure. */
  #envTexture(THREE) {
    const c = document.createElement('canvas');
    c.width = 1024;
    c.height = 512;
    const g = c.getContext('2d');

    const sky = g.createLinearGradient(0, 0, 0, 512);
    sky.addColorStop(0, '#ffffff');
    sky.addColorStop(0.32, '#f3e9d6');
    sky.addColorStop(0.52, '#c9b79a');
    sky.addColorStop(0.56, '#6b5a45');
    sky.addColorStop(1, '#241a12');
    g.fillStyle = sky;
    g.fillRect(0, 0, 1024, 512);

    // overhead soft box
    g.filter = 'blur(18px)';
    g.fillStyle = 'rgba(255,255,255,1)';
    g.fillRect(150, -40, 720, 150);
    g.filter = 'blur(6px)';

    // vertical strip lights and the dark gaps between them
    const strips = [[70, 210], [330, 120], [600, 170], [880, 110]];
    for (const [x, w] of strips) {
      g.fillStyle = 'rgba(255,250,238,0.92)';
      g.fillRect(x, 96, w, 150);
      g.fillStyle = 'rgba(28,20,13,0.55)';
      g.fillRect(x + w + 14, 96, 46, 160);
    }
    g.filter = 'none';

    const tex = new THREE.CanvasTexture(c);
    tex.mapping = THREE.EquirectangularReflectionMapping;
    tex.colorSpace = THREE.SRGBColorSpace;
    return tex;
  }

  /** Faint smudge and micro-scratch, multiplied onto the glass roughness. */
  #imperfectionMap(THREE) {
    const c = document.createElement('canvas');
    c.width = c.height = 512;
    const g = c.getContext('2d');
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, 512, 512);

    for (let i = 0; i < 26; i++) {          // soft smudges
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      const r = 26 + Math.random() * 90;
      const grd = g.createRadialGradient(x, y, 1, x, y, r);
      grd.addColorStop(0, `rgba(150,150,150,${0.05 + Math.random() * 0.09})`);
      grd.addColorStop(1, 'rgba(150,150,150,0)');
      g.fillStyle = grd;
      g.fillRect(x - r, y - r, r * 2, r * 2);
    }
    for (let i = 0; i < 90; i++) {          // hairline scratches
      g.strokeStyle = `rgba(190,190,190,${0.05 + Math.random() * 0.1})`;
      g.lineWidth = 0.5 + Math.random();
      g.beginPath();
      const x = Math.random() * 512;
      const y = Math.random() * 512;
      g.moveTo(x, y);
      g.lineTo(x + (Math.random() - 0.5) * 130, y + (Math.random() - 0.5) * 40);
      g.stroke();
    }
    const t = new THREE.CanvasTexture(c);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
    return t;
  }

  /**
   * Rounded-bottom prism — the liquid's U.
   *
   * Lofts a rounded-rect section up Y, insetting it over the bottom `fillet`
   * on a quarter-circle profile so the column turns into the base instead of
   * ending on a hard rim. Offsetting a rounded rect inward by d is exact:
   * the width loses 2d and the corner radius loses d. Rings are sampled with
   * getSpacedPoints so every ring has the same vertex count regardless of how
   * far its radius has collapsed, which is what keeps the loft quads square.
   */
  #uPrism(THREE, w, h, r, fillet, rings = 12, around = 64) {
    const ringAt = (inset) => {
      const iw = w - 2 * inset;
      const path = this.#roundedPath(THREE, iw, iw, Math.max(0.004, r - inset), false);
      return path.getSpacedPoints(around).slice(0, around);  // drop the repeat
    };

    const levels = [];
    for (let i = 0; i <= rings; i++) {
      const t = (i / rings) * fillet;
      const d = fillet - t;
      levels.push({ y: t, pts: ringAt(fillet - Math.sqrt(Math.max(0, fillet * fillet - d * d))) });
    }
    levels.push({ y: h, pts: ringAt(0) });

    const pos = [];
    for (const l of levels) for (const p of l.pts) pos.push(p.x, l.y, p.y);

    // The section winds counter-clockwise, so (a_i, b_i, a_j) faces outward.
    const idx = [];
    for (let l = 0; l < levels.length - 1; l++) {
      const a = l * around;
      const b = (l + 1) * around;
      for (let i = 0; i < around; i++) {
        const j = (i + 1) % around;
        idx.push(a + i, b + i, a + j, a + j, b + i, b + j);
      }
    }

    const botC = pos.length / 3;
    pos.push(0, 0, 0);
    const topC = pos.length / 3;
    pos.push(0, h, 0);
    const last = (levels.length - 1) * around;
    for (let i = 0; i < around; i++) {
      const j = (i + 1) % around;
      idx.push(botC, i, j);                       // faces -Y
      idx.push(topC, last + j, last + i);         // faces +Y
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(new Float32Array((pos.length / 3) * 2), 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    return geo;
  }

  /**
   * Box-project UVs so the texture's U axis runs up the model's Y.
   *
   * Used for the cap, where U is the wood's grain direction, and for the
   * liquid, where U is depth through the column. With wrapTop off every face
   * including the top samples by height, so the liquid's surface takes the
   * top of its ramp instead of a slice from the middle.
   *
   * ExtrudeGeometry's default generator emits raw model coordinates as UVs,
   * which on this cap run from -0.5 upward — outside [0,1]. Clamped, that
   * smears edge pixels into horizontal bands on some faces while others keep
   * real grain, which is the mismatched-panel look. Projecting each triangle
   * along its dominant normal axis instead puts the texture's U axis, the
   * direction the fibre is drawn in, along world Y on all four sides. The top
   * face carries that run front-to-back, the way one block of wood does.
   *
   * The geometry is non-indexed and flat-shaded, so each vertex already
   * carries its own triangle's normal and the test below is per-face.
   */
  #uprightUVs(geo, size, wrapTop = true, vSize = size) {
    const pos = geo.attributes.position;
    const nor = geo.attributes.normal;
    const uv = geo.attributes.uv;
    const h = vSize / 2;

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const y = pos.getY(i);
      const z = pos.getZ(i);
      const nx = Math.abs(nor.getX(i));
      const ny = Math.abs(nor.getY(i));
      const nz = Math.abs(nor.getZ(i));

      let u;
      let v;
      if (wrapTop && ny > nx && ny > nz) {
        u = (z + h) / vSize;     // top and bottom — grain runs front to back
        v = (x + h) / vSize;
      } else if (nx > nz) {
        u = y / size;            // left and right faces
        v = (z + h) / vSize;
      } else {
        u = y / size;            // front and back faces
        v = (x + h) / vSize;
      }
      uv.setXY(i, u, v);
    }
    uv.needsUpdate = true;
  }

  #woodTexture(THREE) {
    // The real cap is matte dark chocolate with fine fibre. Regular, evenly
    // spaced stripes read as printed plastic, so the strokes are jittered.
    const size = 512;
    const make = (mode) => {
      const c = document.createElement('canvas');
      c.width = size;
      c.height = size;
      const g = c.getContext('2d');
      g.fillStyle = mode === 'bump' ? '#808080' : '#6b4a32';
      g.fillRect(0, 0, size, size);

      for (let i = 0; i < 1400; i++) {
        const y = Math.random() * size;
        const len = 40 + Math.random() * 300;
        const x = Math.random() * size;
        const v = Math.random();
        g.strokeStyle =
          mode === 'bump'
            ? `rgba(${v > 0.5 ? 255 : 0},${v > 0.5 ? 255 : 0},${v > 0.5 ? 255 : 0},${0.05 + Math.random() * 0.12})`
            : `rgba(${34 + v * 78},${22 + v * 54},${12 + v * 36},${0.18 + Math.random() * 0.5})`;
        g.lineWidth = 0.4 + Math.random() * 1.9;
        g.beginPath();
        g.moveTo(x, y);
        g.bezierCurveTo(x + len * 0.35, y + (Math.random() - 0.5) * 3,
                        x + len * 0.7, y + (Math.random() - 0.5) * 3,
                        x + len, y + (Math.random() - 0.5) * 2);
        g.stroke();
      }
      // open pores
      for (let i = 0; i < 500; i++) {
        g.fillStyle = mode === 'bump' ? 'rgba(0,0,0,0.16)' : 'rgba(38,24,14,0.3)';
        g.fillRect(Math.random() * size, Math.random() * size,
                   1 + Math.random() * 5, 0.6 + Math.random());
      }
      const t = new THREE.CanvasTexture(c);
      if (mode !== 'bump') t.colorSpace = THREE.SRGBColorSpace;
      t.anisotropy = 8;
      // The fibre is drawn along the canvas's x axis, so the texture's U axis
      // is the grain direction. #grainUVs points U at world Y, which is what
      // stands the grain up — rotating the texture here instead sent UVs
      // outside [0,1], where clamping smeared them into bands.
      t.wrapS = t.wrapT = THREE.RepeatWrapping;
      return t;
    };
    return { map: make('map'), bump: make('bump') };
  }

  /**
   * The flat label spans two face-widths: it covers the front and wraps half
   * of each side, never the back. Laid out like the real artwork — tagline
   * far left, name and Urdu centre (so they land on the front face), the
   * Azahrah wordmark far right.
   */
  /**
   * Absorption map for the perfume — Beer-Lambert over the real path length.
   *
   * A transmissive liquid is not available here: three.js renders
   * transmissive materials against the opaque pass only, so glass and liquid
   * cannot refract each other, and a transmissive liquid would drop out of
   * the glass's sample and vanish. The liquid therefore stays opaque and the
   * absorption is solved per texel instead.
   *
   * The previous version varied only with height, which is why it read as an
   * ombre: a vertical fade is a gradient, not a volume. What makes a liquid
   * look like a liquid is that the path a ray takes through it is longest
   * near the corners, where it crosses the column diagonally, and shortest
   * through the middle of a face. So path length varies on both axes here —
   * u is height, v runs across each face — and the colour is the surviving
   * transmittance exp(-sigma * path), with sigma solved from the scent so
   * that a unit path reproduces its stated colour exactly. Path stays inside
   * roughly 0.8–1.5: enough that a corner reads deeper than a face, not so
   * much that the perfume goes muddy. Kept deliberately shallow — the depth
   * cue should be felt, not looked at.
   */
  #liquidTexture(THREE, scent) {
    const CW = 256;   // u — height up the column
    const CH = 128;   // v — across one face, 0 and 1 landing on the corners
    const c = document.createElement('canvas');
    c.width = CW;
    c.height = CH;
    const g = c.getContext('2d');

    // Lifted toward white before the absorption is solved, so the perfume
    // reads thin and lit rather than as a heavy pigment.
    const base = new THREE.Color(scent.liquid || '#cfbb67').lerp(new THREE.Color(0xffffff), 0.30);
    const sigma = [base.r, base.g, base.b].map((ch) => -Math.log(Math.max(0.05, ch)));

    const img = g.createImageData(CW, CH);
    for (let y = 0; y < CH; y++) {
      const v = y / (CH - 1);
      // Longest across the corners, shortest through the middle of a face.
      const lat = 0.95 + 0.26 * Math.pow(Math.abs(v - 0.5) * 2, 2.4);
      for (let x = 0; x < CW; x++) {
        const u = x / (CW - 1);
        const depth = 0.92 - 0.13 * u;      // more column beneath, lower down
        const path = lat * depth;
        const o = (y * CW + x) * 4;
        for (let ch = 0; ch < 3; ch++) {
          img.data[o + ch] = 255 * Math.exp(-sigma[ch] * path);
        }
        img.data[o + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);

    const t = new THREE.CanvasTexture(c);
    t.colorSpace = THREE.SRGBColorSpace;
    t.anisotropy = 8;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    return t;
  }

  #labelTexture(THREE, scent) {
    const CW = 1024;
    const CH = 266;
    const c = document.createElement('canvas');
    c.width = CW;
    c.height = CH;
    const g = c.getContext('2d');

    g.fillStyle = scent.label;
    g.fillRect(0, 0, CW, CH);
    for (let i = 0; i < 4200; i++) {        // matte textured stock
      g.fillStyle = `rgba(255,255,255,${Math.random() * 0.045})`;
      g.fillRect(Math.random() * CW, Math.random() * CH, 2, 2);
    }

    const ink = 'rgba(255,255,255,0.93)';

    // tagline — far left, lands on the left side face
    if (scent.tagline) {
      g.fillStyle = 'rgba(255,255,255,0.82)';
      g.font = '17px Georgia, "Times New Roman", serif';
      g.textAlign = 'left';
      const words = scent.tagline.split(' ');
      const lines = [];
      let line = '';
      for (const w of words) {
        if ((line + ' ' + w).trim().length > 22) {
          lines.push(line.trim());
          line = w;
        } else line += ' ' + w;
      }
      lines.push(line.trim());
      lines.slice(0, 4).forEach((l, i) => g.fillText(l, CW * 0.045, CH * 0.50 + i * 21));
    }

    // Front face spans x 287..737 on this texture. Measured off the real
    // bottle: the name sits left-aligned in the upper quarter, and the Urdu is
    // markedly larger, lower, and ranged right across most of the face.
    const F0 = CW * 0.28;
    const FW = CW * 0.44;

    g.textAlign = 'left';
    g.fillStyle = ink;
    g.font = '46px Georgia, "Times New Roman", serif';
    g.fillText(scent.name, F0 + FW * 0.04, CH * 0.34);

    if (scent.urdu) {
      g.textAlign = 'right';
      g.fillStyle = 'rgba(86,66,42,0.4)';
      g.font = '66px "Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Geeza Pro", serif';
      g.fillText(scent.urdu, F0 + FW * 0.95, CH * 0.86);
    }

    // wordmark — far right, lands on the right side face
    g.textAlign = 'right';
    g.fillStyle = ink;
    g.font = '30px Georgia, "Times New Roman", serif';
    g.fillText('Azahrah', CW * 0.965, CH * 0.72);

    const tex = new THREE.CanvasTexture(c);
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.anisotropy = 8;
    return tex;
  }

  // ---- geometry -------------------------------------------------------

  /**
   * Label strip following the bottle's rounded-rect perimeter, from the middle
   * of the left face, around the front, to the middle of the right face.
   * Flat panels float off the corner radius and read as detached cards.
   */
  #labelStrip(THREE, a, r, h, lift) {
    const pts = [];
    const R = r + lift;
    const A = a + lift;
    const push = (x, z) => pts.push([x, z]);

    push(-A, 0);
    push(-A, a - r);
    const seg = 7;
    for (let i = 0; i <= seg; i++) {          // front-left corner
      const t = Math.PI - (i / seg) * (Math.PI / 2);
      push(-(a - r) + Math.cos(t) * R, (a - r) + Math.sin(t) * R);
    }
    push(a - r, A);
    for (let i = 0; i <= seg; i++) {          // front-right corner
      const t = (Math.PI / 2) - (i / seg) * (Math.PI / 2);
      push((a - r) + Math.cos(t) * R, (a - r) + Math.sin(t) * R);
    }
    push(A, a - r);
    push(A, 0);

    const lens = [0];
    for (let i = 1; i < pts.length; i++) {
      const dx = pts[i][0] - pts[i - 1][0];
      const dz = pts[i][1] - pts[i - 1][1];
      lens.push(lens[i - 1] + Math.hypot(dx, dz));
    }
    const total = lens[lens.length - 1];

    const pos = [];
    const uv = [];
    const idx = [];
    for (let i = 0; i < pts.length; i++) {
      const [x, z] = pts[i];
      const u = lens[i] / total;
      pos.push(x, h / 2, z, x, -h / 2, z);
      uv.push(u, 1, u, 0);
    }
    for (let i = 0; i < pts.length - 1; i++) {
      const b = i * 2;
      idx.push(b, b + 1, b + 2, b + 1, b + 3, b + 2);
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
    geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
    geo.setIndex(idx);
    geo.computeVertexNormals();
    return geo;
  }

  #roundedPath(THREE, w, d, r, hole) {
    const p = hole ? new THREE.Path() : new THREE.Shape();
    const x = w / 2;
    const y = d / 2;
    // holes must wind opposite to the outline or the triangulation inverts
    const pts = [
      [-x + r, -y], [x - r, -y], [x, -y], [x, -y + r],
      [x, y - r], [x, y], [x - r, y],
      [-x + r, y], [-x, y], [-x, y - r],
      [-x, -y + r], [-x, -y],
    ];
    if (hole) {
      p.moveTo(-x, -y + r);
      p.quadraticCurveTo(-x, -y, -x + r, -y);
      p.lineTo(x - r, -y);
      p.quadraticCurveTo(x, -y, x, -y + r);
      p.lineTo(x, y - r);
      p.quadraticCurveTo(x, y, x - r, y);
      p.lineTo(-x + r, y);
      p.quadraticCurveTo(-x, y, -x, y - r);
      p.closePath();
    } else {
      p.moveTo(-x + r, -y);
      p.lineTo(x - r, -y);
      p.quadraticCurveTo(x, -y, x, -y + r);
      p.lineTo(x, y - r);
      p.quadraticCurveTo(x, y, x - r, y);
      p.lineTo(-x + r, y);
      p.quadraticCurveTo(-x, y, -x, y - r);
      p.lineTo(-x, -y + r);
      p.quadraticCurveTo(-x, -y, -x + r, -y);
    }
    void pts;
    return p;
  }

  #extrude(THREE, shape, h, bevel) {
    const geo = new THREE.ExtrudeGeometry(shape, {
      depth: h,
      bevelEnabled: bevel > 0,
      bevelThickness: bevel,
      bevelSize: bevel,
      bevelSegments: 3,
      curveSegments: 10,
    });
    geo.rotateX(-Math.PI / 2); // extrude along +Y
    geo.computeVertexNormals();
    return geo;
  }

  /**
   * Rounded prism, exactly w x d x h.
   *
   * bevelSize expands an extrusion outward by 2 x bevelSize, so a bevelled
   * solid built from a full-width shape ends up wider than its neighbours —
   * that was the flare at the bottle's top and bottom. Inset the shape by the
   * bevel so the bevel lands on the intended width, and shift the result back
   * to start at y = 0.
   */
  #prism(THREE, w, d, h, r, bevel) {
    if (!bevel) return this.#extrude(THREE, this.#roundedPath(THREE, w, d, r, false), h, 0);
    const b = Math.min(bevel, w / 4, d / 4, h / 4);
    const shape = this.#roundedPath(THREE, w - 2 * b, d - 2 * b, Math.max(0.004, r - b), false);
    const geo = this.#extrude(THREE, shape, h - 2 * b, b);
    geo.translate(0, b, 0);
    return geo;
  }

  /** Walls only — an outer shape with a hole gives real glass thickness. */
  #hollow(THREE, w, cav, h, r, cavR) {
    const shape = this.#roundedPath(THREE, w, w, r, false);
    shape.holes.push(this.#roundedPath(THREE, cav, cav, cavR, true));
    return this.#extrude(THREE, shape, h, 0);
  }

  // ---- scene ----------------------------------------------------------

  async #init() {
    this.ready = true;
    const THREE = await import(this.dataset.three);
    this.THREE = THREE;

    const renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: false,
      antialias: !this.lowPower,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.lowPower ? 1.5 : 2));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.06;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.setClearColor(new THREE.Color(this.dataset.bg || '#efe4ce'), 1);
    this.renderer = renderer;

    const scene = new THREE.Scene();
    this.scene = scene;

    const pmrem = new THREE.PMREMGenerator(renderer);
    let envTex = null;
    if (this.dataset.env) {
      try {
        envTex = await new THREE.TextureLoader().loadAsync(this.dataset.env);
        envTex.mapping = THREE.EquirectangularReflectionMapping;
        envTex.colorSpace = THREE.SRGBColorSpace;
      } catch {
        envTex = null;
      }
    }
    // A painted gradient is why glass read as CGI; a real room gives it
    // something with structure to reflect. Falls back if the file fails.
    if (!envTex) envTex = this.#envTexture(THREE);
    scene.environment = pmrem.fromEquirectangular(envTex).texture;
    envTex.dispose();
    pmrem.dispose();

    this.camera = new THREE.PerspectiveCamera(26, 1, 0.1, 100);
    this.camera.position.set(0, 0, 9.0);

    // The assembly is built from y=0 at the base upward, so recentre it.
    this.centerY = -TOTAL_H / 2;

    scene.add(new THREE.AmbientLight(0xfff3e2, 0.1));
    const key = new THREE.DirectionalLight(0xfff6ea, 0.42);
    key.position.set(2.4, 6.2, 3.2);
    scene.add(key);
    // cool rim from behind, so the edges read against a warm ground
    const rim = new THREE.DirectionalLight(0xcfd8e4, 0.5);
    rim.position.set(-3.4, 1.6, -4.2);
    scene.add(rim);
    const fill = new THREE.DirectionalLight(0xf4e3c8, 0.16);
    fill.position.set(-2.2, -1.4, 2.6);
    scene.add(fill);

    const bgTex = (() => {
      const c = document.createElement('canvas');
      c.width = 512;
      c.height = 256;
      const g = c.getContext('2d');
      g.fillStyle = this.dataset.bg || '#efe4ce';
      g.fillRect(0, 0, 512, 256);
      // A pool of light behind the bottle only. A lift spread across the whole
      // plane makes the canvas read as a lighter rectangle on the page; this
      // fades back to the page colour well inside the frame.
      const pool = g.createRadialGradient(256, 118, 4, 256, 118, 56);
      pool.addColorStop(0.0, 'rgba(255,255,255,0.34)');
      pool.addColorStop(0.45, 'rgba(255,255,255,0.11)');
      pool.addColorStop(1.0, 'rgba(255,255,255,0)');
      g.fillStyle = pool;
      g.fillRect(0, 0, 512, 256);
      const shade = g.createRadialGradient(256, 160, 5, 256, 160, 40);
      shade.addColorStop(0.0, 'rgba(84,64,40,0.18)');
      shade.addColorStop(1.0, 'rgba(84,64,40,0)');
      g.fillStyle = shade;
      g.fillRect(0, 0, 512, 256);
      const t = new THREE.CanvasTexture(c);
      t.colorSpace = THREE.SRGBColorSpace;
      return t;
    })();
    // Sized to cover the frustum at any aspect, with the pool fading inside it.
    const backdrop = new THREE.Mesh(
      new THREE.PlaneGeometry(22, 13),
      new THREE.MeshBasicMaterial({ map: bgTex, toneMapped: false })
    );
    backdrop.position.z = -8;
    scene.add(backdrop);

    const shadowTex = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 256;
      const g = c.getContext('2d');
      const grd = g.createRadialGradient(128, 128, 4, 128, 128, 124);
      grd.addColorStop(0, 'rgba(60,44,26,0.5)');
      grd.addColorStop(0.45, 'rgba(60,44,26,0.22)');
      grd.addColorStop(1, 'rgba(60,44,26,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, 256, 256);
      const t = new THREE.CanvasTexture(c);
      t.colorSpace = THREE.SRGBColorSpace;
      return t;
    })();
    const contact = new THREE.Mesh(
      new THREE.PlaneGeometry(3.1, 2.0),
      new THREE.MeshBasicMaterial({
        map: shadowTex,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      })
    );
    contact.rotation.x = -Math.PI / 2;
    contact.position.y = -TOTAL_H / 2 + 0.005;
    contact.renderOrder = -1;
    scene.add(contact);
    this.contact = contact;

    // Black flags either side — the studio technique for photographing glass.
    // Their x is derived from the frustum in #resize, because a fixed offset
    // slides into view as soon as the canvas gets narrower.
    const flagMat = new THREE.MeshBasicMaterial({ color: 0x120d08, toneMapped: false });
    this.flags = [];
    for (const sx of [-1, 1]) {
      const flag = new THREE.Mesh(new THREE.PlaneGeometry(3.4, 10), flagMat);
      flag.position.set(sx * 4, 0, 2.4);
      flag.rotation.y = sx * -1.0;
      flag.userData.sx = sx;
      scene.add(flag);
      this.flags.push(flag);
    }
    // a bright strip above, so the shoulders and cap edge catch a highlight
    const strip = new THREE.Mesh(
      new THREE.PlaneGeometry(4.2, 1.6),
      new THREE.MeshBasicMaterial({ color: 0xfff6e8, toneMapped: false })
    );
    strip.position.set(0, 3.4, 1.6);
    strip.rotation.x = Math.PI / 2.6;
    scene.add(strip);

    const group = new THREE.Group();
    this.group = group;
    scene.add(group);

    // Transmission is the expensive part; drop it on weak hardware.
    // thickness drives the screen-space refraction offset, so it has to match
    // each part's real optical path. One blanket value sized for solid glass
    // smeared a displaced ghost of the label across the thin walls.
    const rough = this.#imperfectionMap(THREE);
    const glassBase = {
      color: 0xffffff,
      metalness: 0,
      roughness: 0.035,
      roughnessMap: rough,
      ior: 1.48,
      reflectivity: 0.5,
    };
    const glassExtra = this.lowPower
      ? { transparent: true, opacity: 0.34, envMapIntensity: 2.0 }
      : {
          transmission: 1,
          dispersion: 0.035,
          envMapIntensity: 0.55,
          specularIntensity: 1,
          attenuationColor: new THREE.Color('#dfe9df'),
          attenuationDistance: 3.2,
        };

    // walls are 0.04 thick
    this.glassMat = new THREE.MeshPhysicalMaterial({
      ...glassBase,
      ...glassExtra,
      ...(this.lowPower ? {} : { thickness: 0.08 }),
    });
    // base and shoulder really are solid glass across the full width
    this.glassSolid = new THREE.MeshPhysicalMaterial({
      ...glassBase,
      ...glassExtra,
      ...(this.lowPower ? {} : { thickness: 0.55 }),
    });

    // Built from y = 0 at the base upward.
    const yBase = 0;
    const yCav = BASE_H;
    const yShoulder = BASE_H + CAV_H;

    // Solid glass base. Overlaps the cavity slightly so the wall mesh's
    // bottom ring isn't coplanar with it (which would z-fight).
    const base = new THREE.Mesh(
      this.#prism(THREE, W, W, BASE_H + 0.012, CORNER, 0.022),
      this.glassSolid
    );
    base.position.y = yBase;
    group.add(base);

    // Glass around the liquid's rounded bottom. Solid across the full width,
    // so the U is bedded in glass rather than leaving an air wedge in the
    // cavity corners. It shares glassMat with the walls above it, so the two
    // carry the same transmission tint and the join does not show.
    const bowl = new THREE.Mesh(
      this.#prism(THREE, W, W, CAV_FILLET + 0.012, CORNER, 0),
      this.glassMat
    );
    bowl.position.y = yCav;
    group.add(bowl);

    // The hollow walls, starting where the fillet lets go of the liquid.
    const walls = new THREE.Mesh(
      this.#hollow(THREE, W, CAV_W, CAV_H - CAV_FILLET, CORNER, 0.1),
      this.glassMat
    );
    walls.position.y = yCav + CAV_FILLET;
    group.add(walls);

    const shoulder = new THREE.Mesh(
      this.#prism(THREE, W, W, SHOULDER_H + 0.02, CORNER, 0),
      this.glassSolid
    );
    shoulder.position.y = yShoulder - 0.006;
    group.add(shoulder);

    const neck = new THREE.Mesh(
      this.#prism(THREE, NECK_W, NECK_W, NECK_H, 0.02, 0),
      this.glassSolid
    );
    neck.position.y = BODY_H - 0.015;
    group.add(neck);

    // The clearcoat that used to sit on this was the plastic-insert look: a
    // hard second specular layer over a flat colour. Depth now comes from the
    // ramp, and the wet highlight from a low roughness against the studio env.
    this.liquidTextures = this.scents.map((sc) => this.#liquidTexture(THREE, sc));
    this.liquidMat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,             // the map carries the colour
      map: this.liquidTextures[0],
      roughness: 0.05,
      metalness: 0,
      envMapIntensity: 1.35,
      specularIntensity: 1,
    });

    // Sized a touch over the cavity so it beds into the inner wall instead of
    // leaving an air gap — that gap was the outline reading as a loose block.
    const LIQ_W = CAV_W + 0.006;
    const liquidGeo = this.#uPrism(THREE, LIQ_W, CAV_H * FILL, 0.1, CAV_FILLET);
    this.#uprightUVs(liquidGeo, CAV_H * FILL, false, LIQ_W);
    const liquid = new THREE.Mesh(liquidGeo, this.liquidMat);
    liquid.position.y = yCav;
    group.add(liquid);

    // Dip tube: the atomiser stem, visible mainly through refraction.
    const tube = new THREE.Mesh(
      new THREE.CylinderGeometry(0.028, 0.024, CAV_H * 0.9, 10, 1, true),
      this.liquidMat
    );
    tube.position.set(0, yCav + CAV_H * 0.45, 0);
    group.add(tube);
    this.dipTube = tube;

    const woodMaps = this.#woodTexture(THREE);
    const capMat = new THREE.MeshPhysicalMaterial({
      map: woodMaps.map,
      bumpMap: woodMaps.bump,
      bumpScale: 0.75,
      roughnessMap: woodMaps.bump,
      roughness: 0.92,
      metalness: 0,
      envMapIntensity: 0.7,
      clearcoat: 0.16,
      clearcoatRoughness: 0.7,
      sheen: 0.25,
      sheenRoughness: 0.9,
      sheenColor: new THREE.Color('#6b4a30'),
    });
    const capGeo = this.#prism(THREE, CAP_W, CAP_W, CAP_H, 0.03, CAP_BEVEL);
    this.#uprightUVs(capGeo, CAP_W);
    const cap = new THREE.Mesh(capGeo, capMat);
    cap.position.y = BODY_H - 0.02;
    group.add(cap);

    // The label covers the front face and wraps half of each side. A box put
    // label colour on the back too, which the real one never touches.
    this.labelTextures = this.scents.map((s) => this.#labelTexture(THREE, s));
    this.labelMat = new THREE.MeshStandardMaterial({
      roughness: 0.95,
      metalness: 0,
      envMapIntensity: 0.4,
      side: THREE.DoubleSide,
    });
    const labelY = BODY_H - LABEL_DROP - LABEL_H / 2;
    const sleeve = new THREE.Mesh(
      this.#labelStrip(THREE, W / 2, CORNER, LABEL_H, 0.0025),
      this.labelMat
    );
    sleeve.position.y = labelY;
    group.add(sleeve);

    this.#applyScent(0, true);
    this.#resize();
    this.ro = new ResizeObserver(() => this.#resize());
    this.ro.observe(this.canvas);

    this.tick = this.#tick.bind(this);
    if (this.reduced) {
      this.group.rotation.set(0.06, -0.5, 0);
      this.group.position.y = this.centerY;
      this.#renderOnce();
    } else {
      this.#start();
    }
  }

  #applyScent(i, force) {
    if (i === this.index && !force) return;
    this.index = i;
    const s = this.scents[i];

    this.labelMat.map = this.labelTextures[i];
    this.labelMat.needsUpdate = true;
    this.liquidMat.map = this.liquidTextures[i];
    this.liquidMat.needsUpdate = true;


    if (this.captionEl) {
      this.captionEl.dataset.index = String(i);
      this.captionEl.querySelectorAll('[data-scent-item]').forEach((el, n) => {
        el.toggleAttribute('data-active', n === i);
      });
    }
  }

  #resize() {
    if (!this.renderer) return;
    // Measure the canvas itself, not the stage. The canvas is laid out in one
    // grid column, so sizing the buffer to the full-width stage squeezed it
    // non-uniformly and stretched the bottle vertically.
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (!w || !h) return;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    // Keep the flags just outside the visible frustum at their own depth.
    if (this.flags) {
      const z = 2.4;
      const halfH = Math.tan((this.camera.fov * Math.PI) / 360) * (this.camera.position.z - z);
      const halfW = halfH * this.camera.aspect;
      const clear = halfW + 3.4 * Math.cos(1.0) * 0.5 + 0.35;
      for (const f of this.flags) f.position.x = f.userData.sx * clear;
    }
  }

  #scrollProgress() {

    const track = this.querySelector('.az-bottle__track') || this;
    const r = track.getBoundingClientRect();
    const total = r.height - window.innerHeight;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(1, -r.top / total));
  }

  #renderOnce() {
    this.renderer.render(this.scene, this.camera);
  }

  #start() {
    if (this.running || !this.renderer || this.reduced) return;
    this.running = true;
    this.raf = requestAnimationFrame(this.tick);
  }

  #stop() {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
  }

  /**
   * Maps overall scroll to a stage and a local 0..1 within it.
   *
   * Each stage is exactly one turn, so at every boundary the bottle is back
   * at the orientation it started in — which makes the handover invisible
   * without the old "wait until the back faces camera" gate. That gate was
   * the timing bug: it fired instantly on odd boundaries and stalled 74% of
   * a stage on even ones, so two fragrances got 29% of the scroll and two
   * got 4.3%.
   *
   * The turn starts with the back to camera, so the label sweeps into view
   * and is front-on at local 0.5 — the peak of each ingredient scene.
   */
  #timeline(progress, n) {
    const scaled = Math.max(0, Math.min(n, progress * n));
    let stage = Math.floor(scaled);
    let local = scaled - stage;
    if (stage >= n) { stage = n - 1; local = 1; }
    return { stage, local, rotY: Math.PI + local * Math.PI * 2 };
  }

  #tick(now) {
    if (!this.running) return;

    const t = (now || 0) / 1000;
    const dt = this.lastT ? Math.min(0.05, t - this.lastT) : 0.016;
    this.lastT = t;

    // Frame-rate independent ease. The old per-frame 0.08 settled twice as
    // fast at 120Hz as at 60Hz.
    const target = this.#scrollProgress();
    this.progress += (target - this.progress) * (1 - Math.exp(-dt / 0.085));

    const n = this.scents.length;
    const { stage, local, rotY } = this.#timeline(this.progress, n);

    this.group.rotation.y = rotY;
    this.group.rotation.x = 0.05 + Math.sin(local * Math.PI) * 0.05;

    // TEMP-INSPECT: ?pose=front|three|side fixes the view for comparison
    // against the reference photograph. Does not affect the production
    // start rotation — it only overrides while the flag is present.
    const pose = new URLSearchParams(location.search).get('pose');
    if (pose) {
      this.group.rotation.y = pose === 'side' ? Math.PI / 2 : pose === 'three' ? -Math.PI / 4 : 0;
      this.group.rotation.x = 0;
      this.group.position.y = this.centerY;
      this.group.scale.setScalar(1);
    }
    this.group.position.y = this.centerY + Math.sin(local * Math.PI) * 0.035;

    // A settle on entry and a breath on exit, so stages hand over rather
    // than cutting. Geometry is identical across fragrances, so nothing
    // needs to fade.
    const enter = Math.min(1, local / 0.14);
    const leave = local > 0.9 ? 1 - (local - 0.9) / 0.1 : 1;
    const breath = 0.965 + 0.035 * Math.min(enter, leave);
    this.group.scale.setScalar(breath);

    if (stage !== this.index) this.#applyScent(stage);

    this.local = local;
    this.stage = stage;

    this.renderer.render(this.scene, this.camera);
    this.raf = requestAnimationFrame(this.tick);
  }
}

if (!customElements.get('az-bottle')) customElements.define('az-bottle', AzBottle);
